import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { NeuteredEnum } from '@src/components/PatientInformation/PatientInformation.helpers';
import { mountDevTools } from '@src/store/utils/mount-dev-tools';
import { deserializeDates, safeDeserialize } from '@src/store/utils/serialize-dates';
import { ReasonForVisitType } from '@src/store/modules/reason-for-visit.store';

type PatientInformationState = {
	species: string;
	petName: string;
	sex: string;
	neutered: NeuteredEnum | string;
	birthDate: Date | null;
	isEstimatedAge: boolean;
	breed: string;
	color: string;
	patientId: string;
	notes: Record<string, any>;
	checkInLink: string;
	notesReasonForVisit: ReasonForVisitType | null;
};

type PatientInformationActions = {
	setSpecies: (species: string) => void;
	setPetName: (petName: string) => void;
	setSex: (sex: string) => void;
	setNeutered: (spayed: string) => void;
	setBirthDate: (birthDate: Date | null) => void;
	setIsEstimatedAge: (isEstimatedAge: boolean) => void;
	setBreed: (breed: string) => void;
	setColor: (color: string) => void;
	setPatientId: (patientId: string) => void;
	setNotes: (notes: Record<string, any>) => void;
	setNotesReasonForVisit: (reason: ReasonForVisitType | null) => void;
	setCheckInLink: (checkInLink: string) => void;
	setAllPatientInformation: (patientInfo: Partial<PatientInformationState>) => void;
	reset: () => void;
};

const INITIAL_STATE: PatientInformationState = {
	species: '',
	petName: '',
	sex: '',
	neutered: '',
	birthDate: null,
	isEstimatedAge: false,
	breed: '',
	color: '',
	patientId: '',
	notes: {},
	checkInLink: '',
	notesReasonForVisit: null,
};

const usePatientInformationStore = create<PatientInformationState & PatientInformationActions>()(
	persist(
		(set) => ({
			...INITIAL_STATE,
			setSpecies: (species) => set({ species }),
			setPetName: (petName) => set({ petName }),
			setSex: (sex) => set({ sex }),
			setNeutered: (neutered) => set({ neutered }),
			setBirthDate: (birthDate) => set({ birthDate }),
			setIsEstimatedAge: (isEstimatedAge) => set({ isEstimatedAge }),
			setBreed: (breed) => set({ breed }),
			setColor: (color) => set({ color }),
			setPatientId: (patientId) => set({ patientId }),
			setNotes: (notes) => set({ notes }),
			setNotesReasonForVisit: (notesReasonForVisit) => set({ notesReasonForVisit }),
			setCheckInLink: (checkInLink) => set({ checkInLink }),
			setAllPatientInformation: (patientInfo) => set((prevState) => ({ ...prevState, ...patientInfo })),
			reset: () => set(INITIAL_STATE),
		}),
		{
			name: 'book-an-appointment-patient-information',
			storage: {
				getItem: (name) => {
					const str = sessionStorage.getItem(name);
					if (!str) return null;
					return safeDeserialize(
						str,
						(state) =>
							deserializeDates(state, ['birthDate' as keyof PatientInformationState], INITIAL_STATE),
						INITIAL_STATE
					);
				},
				setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
				removeItem: (name) => sessionStorage.removeItem(name),
			},
		}
	)
);

mountDevTools('usePatientInformationStore', usePatientInformationStore);

export default usePatientInformationStore;
